import {FC, useEffect, useMemo, useState} from 'react'
import {FieldArray, Field} from 'formik'
import {Select} from './Select'
import {MultiSelect} from './MultiSelect'
import {Switch} from './CustomInput'
import {SelectFile} from './SelectFile'
import {FieldProps} from './Form'
import {useQuery} from '@tanstack/react-query'
import {v1_get_request_api} from '../api/get'

type MultiInputFieldProps = {
  formik: any
  f: FieldProps
  value: any
  index: number
  name: string
  onChange: (val: any) => void
}

export const MultiInputField: FC<MultiInputFieldProps> = ({
  formik,
  f,
  value,
  index,
  name,
  onChange,
}) => {
  const iFieldKey = f.name.replace(`${name}.`, '')
  const fieldName = `${name}[${index}].${iFieldKey}`
  const meta = formik.getFieldMeta(fieldName)
  const fieldValue =
    typeof meta.value === 'object' && meta.value !== null
      ? 'id' in meta.value && typeof meta.value.id !== 'object'
        ? meta.value.id
        : ''
      : meta.value

  const {data: result} = useQuery({
    queryKey: f.optionsQuery?.queryKey || [],
    queryFn: v1_get_request_api,
    enabled: f.optionsQuery?.enabled || false,
  })

  const [options, setoptions] = useState<any[]>(f.options || [])

  useEffect(() => {
    if (result && f.optionsQuery) {
      setoptions(
        result.entities.map((item: any) => {
          if (f.optionsQuery?.name) {
            const names = f.optionsQuery.name.split('.')
            let value = item
            names.forEach((n) => {
              if (value) value = value[n]
            })
            return {
              label: value,
              value: item.id,
            }
          }
          return {label: item.name, value: item.id}
        })
      )
    }
  }, [result])

  const selectedOption = useMemo(() => {
    const matched = options.find((opt) => opt.value === fieldValue)
    const fallbackLabel =
      typeof fieldValue === 'object'
        ? fieldValue?.name || formik.getFieldMeta(fieldValue).value
        : fieldValue
    return (
      matched || {
        value: fieldValue,
        label: fallbackLabel,
      }
    )
  }, [fieldValue, options])

  const handleValueChange = (val: any) => {
    const updated = [...formik.values[name]]
    updated[index][iFieldKey] = val
    onChange(updated)
  }

  return (
    <div className='col'>
      <label className='form-label'>{f.label}</label>
      {f.type === 'select' ? (
        <Select
          placeholder={`Select`}
          options={options}
          selected={selectedOption}
          onSelect={(val: {value: any; label: string}) => handleValueChange(val.value)}
        />
      ) : f.type === 'multi-select' ? (
        <MultiSelect
          placeholder={`Select multiple ${f.label}`}
          options={f.options || []}
          selected={
            Array.isArray(fieldValue)
              ? f.options?.filter((opt) =>
                  fieldValue.some((v) =>
                    typeof v === 'object' ? v.id === opt.value : v === opt.value
                  )
                )
              : []
          }
          onSelect={(selected) => handleValueChange(selected)}
        />
      ) : f.type === 'files' ? (
        <SelectFile onSelect={handleValueChange} acceptedTypes={f.acceptedTypes} />
      ) : (
        <Field
          name={fieldName}
          type={f.type || 'text'}
          className='form-control form-control-lg form-control-solid'
          as={f.type === 'textarea' ? 'textarea' : undefined}
          component={f.type === 'switch' ? Switch : undefined}
          disabled={f.disabled}
        />
      )}
    </div>
  )
}

export type OptionsQuery = {
  queryKey: string[]
  enabled: boolean
  name?: string
}

export type MultiInputProps = {
  name: string
  label?: string
  value: any[]
  onChange: (val: any[]) => void
  fields: FieldProps[]
  options?: {label: string; value: any}[]
  optionsQuery?: OptionsQuery
}

export const MultiInput: FC<{field: MultiInputProps; formik: any}> = ({field, formik}) => {
  const {name, label, value, onChange, fields} = field

  return (
    <FieldArray
      name={name}
      render={(arrayHelpers) => (
        <div className='mb-5'>
          {label && <label className='form-label mb-3'>{label}</label>}

          {value?.map((item, idx) => (
            <div className='row mb-3 align-items-end' key={idx}>
              {fields.map((f) => (
                <MultiInputField
                  key={`${f.name}-${idx}`}
                  f={f}
                  formik={formik}
                  value={item}
                  index={idx}
                  name={name}
                  onChange={onChange}
                />
              ))}
              <div className='col-auto'>
                <button
                  type='button'
                  className='btn btn-sm btn-danger'
                  onClick={() => arrayHelpers.remove(idx)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    />
  )
}
